/**
 * The backdrop planet's spin, as pure state the render loop steps (`PlanetStage.ts`): yaw about the
 * planet's axis plus a clamped pitch, a drag that sets both directly, inertia that carries the drag's
 * last velocity after release, and a slow idle spin. No zoom exists.
 *
 * Everything animated runs on one shared clock, `timeScale`: the stage advances the spin, the clouds
 * and the traffic by `dt * timeScale`, never by wall time. When the viewer lets go (and on load) the
 * planet runs a fixed sequence, one step after another:
 *
 *   0. `beat` — a short hold, everything running as it was (a fling keeps coasting);
 *   1. `reorient` — ease back to the rest angle: pitch to {@link REST_PITCH}, the spin to {@link IDLE_YAW};
 *   2. in **pause**, `stop` — `timeScale` eases to 0, so the whole scene glides to a stop;
 *   3. `rest` — nothing moves and the stage stops drawing frames.
 *
 * In **play** steps 0 and 1 run and then the slow `spin` carries on. A grab cancels the sequence at
 * any step and revives the clock at once. Yaw is left free: the spin is the yaw, so steering it to a
 * fixed face would visibly rewind it.
 */

export type SpinMode = "play" | "pause";

export type SpinPhase = "drag" | "beat" | "reorient" | "spin" | "stop" | "rest";

export type Spin = {
  /** Rotation about the planet's own axis, radians. */
  yaw: number;
  /** Tilt toward or away from the viewer, radians, within ±{@link PITCH_LIMIT}. */
  pitch: number;
  /** Angular velocities, radians per second of the shared clock. */
  vYaw: number;
  vPitch: number;
  phase: SpinPhase;
  /** Seconds into the current phase. */
  t: number;
  /** The shared clock's rate, 0..1. */
  timeScale: number;
  /** Where `reorient` or `stop` started, for their eases. */
  from: { pitch: number; vPitch: number; vYaw: number; timeScale: number };
};

/** The idle spin, radians per second: one turn in about three minutes. */
export const IDLE_YAW = 0.035;
export const PITCH_LIMIT = 0.55;
/** The rest angle's tilt, radians. */
export const REST_PITCH = 0.12;
/** Step 0: the hold after letting go before anything settles, seconds. */
export const SETTLE_BEAT = 1.2;
/** Step 1: easing back to the rest angle, seconds. Slow and gentle: it eases in and lands softly. */
export const REORIENT_DURATION = 3;
/**
 * How long the fling's leftover pitch velocity carries into the reorient before the ease takes over,
 * seconds (an exponential time constant). It keeps the hand-off free of a lurch without letting a
 * hard fling overshoot far past the rest angle.
 */
export const REORIENT_HANDOFF = 0.35;
/** Step 2: `timeScale` easing from 1 to 0 in pause, seconds. */
export const STOP_DURATION = 2.4;
/** `timeScale` easing back up to 1 after a grab or on play, seconds. */
export const WAKE_DURATION = 0.5;
/** Below this the clock, and a velocity, count as stopped. */
export const REST_EPSILON = 0.001;
/** Radians per dragged pixel, at a planet radius of 500px (scaled by the actual radius). */
const RAD_PER_PX = 1 / 500;
/** How fast a fling decays back to the idle spin, per second. */
const FRICTION = 1.6;
/** Fling speed cap, radians per second, so a wild flick doesn't turn into a blur. */
const MAX_V = 6;

/** On load the planet is alive and runs the settle sequence from its beat. */
export function restingSpin(): Spin {
  return { yaw: 0, pitch: REST_PITCH, vYaw: IDLE_YAW, vPitch: 0, phase: "beat", t: 0, timeScale: 1, from: { pitch: REST_PITCH, vPitch: 0, vYaw: IDLE_YAW, timeScale: 1 } };
}

const clampPitch = (p: number) => Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, p));
const clampV = (v: number) => Math.max(-MAX_V, Math.min(MAX_V, v));
const smoothstep = (u: number) => u * u * (3 - 2 * u);
/** Ken Perlin's smootherstep: zero velocity and zero acceleration at both ends. */
const smootherstep = (u: number) => u * u * u * (u * (u * 6 - 15) + 10);
const smootherstepSlope = (u: number) => 30 * u * u * (u - 1) * (u - 1);

/** Start a drag: the sequence is cancelled and the planet stops where it is and follows the pointer. */
export function grab(s: Spin): Spin {
  return { ...s, vYaw: 0, vPitch: 0, phase: "drag", t: 0 };
}

/**
 * Follow a pointer move of (`dx`, `dy`) pixels over `dt` seconds on a planet `radius` pixels across.
 * Dragging right turns the near face right; dragging down tips the top toward the viewer.
 */
export function drag(s: Spin, move: { dx: number; dy: number; dt: number; radius: number }): Spin {
  const k = RAD_PER_PX * (500 / Math.max(move.radius, 1));
  const dYaw = move.dx * k;
  const pitch = clampPitch(s.pitch + move.dy * k);
  const dt = Math.max(move.dt, 1 / 240);
  // Velocity is smoothed over recent moves so the release carries the gesture, not its last jitter.
  return {
    ...s,
    yaw: s.yaw + dYaw,
    pitch,
    vYaw: clampV(s.vYaw * 0.5 + (dYaw / dt) * 0.5),
    vPitch: clampV(s.vPitch * 0.5 + ((pitch - s.pitch) / dt) * 0.5),
  };
}

/** End a drag: inertia takes over and the settle sequence starts at its beat. */
export function release(s: Spin): Spin {
  return { ...s, phase: "beat", t: 0 };
}

/** At rest: the clock has stopped and the stage can stop drawing. */
export function atRest(s: Spin): boolean {
  return s.phase === "rest";
}

/**
 * True while the motion is quick enough to need every display frame: a drag, a coasting fling, or
 * the reorient. The slow spin and the glide to a stop can be drawn at a capped rate.
 */
export function needsFullRate(s: Spin): boolean {
  if (s.phase === "drag" || s.phase === "reorient") return true;
  return s.phase === "beat" && (Math.abs(s.vYaw - IDLE_YAW) > 0.02 || Math.abs(s.vPitch) > 0.02);
}

/**
 * Advance `dt` seconds of wall time in `mode`. The clock itself (`timeScale`) is what moves in wall
 * time; every motion advances by `dt * timeScale`.
 */
export function stepSpin(s: Spin, dt: number, mode: SpinMode): Spin {
  if (s.phase === "rest") return mode === "play" ? { ...s, phase: "spin", t: 0, vYaw: IDLE_YAW } : s;
  if (s.phase === "stop") {
    if (mode === "play") return stepSpin({ ...s, phase: "spin", t: 0 }, dt, mode);
    const t = s.t + dt;
    const timeScale = s.from.timeScale * (1 - smoothstep(Math.min(t / STOP_DURATION, 1)));
    if (timeScale < REST_EPSILON) return { ...s, phase: "rest", t: 0, timeScale: 0 };
    return { ...s, t, timeScale, yaw: s.yaw + s.vYaw * dt * timeScale };
  }

  // Every other phase wakes the clock back toward 1.
  const woken = s.timeScale + dt / WAKE_DURATION;
  const timeScale = woken > 1 - REST_EPSILON ? 1 : woken;
  const sdt = dt * timeScale;
  if (s.phase === "drag") return { ...s, timeScale };

  if (s.phase === "spin") {
    if (mode === "pause") return startStop({ ...s, timeScale });
    return { ...s, timeScale, t: s.t + sdt, vYaw: IDLE_YAW, yaw: s.yaw + IDLE_YAW * sdt };
  }

  if (s.phase === "beat") {
    // Inertia: yaw velocity eases toward the idle spin and pitch velocity toward none.
    const decay = Math.exp(-FRICTION * sdt);
    const vYaw = IDLE_YAW + (s.vYaw - IDLE_YAW) * decay;
    const vPitch = s.vPitch * decay;
    const free = s.pitch + vPitch * sdt;
    const pitch = clampPitch(free);
    // Hitting the pitch limit kills the pitch fling rather than pinning against the stop.
    const next = { ...s, timeScale, t: s.t + sdt, yaw: s.yaw + vYaw * sdt, pitch, vYaw, vPitch: pitch === free ? vPitch : 0 };
    if (next.t < SETTLE_BEAT) return next;
    // Already at the rest angle: nothing to reorient, go straight on.
    if (Math.abs(next.pitch - REST_PITCH) < REST_EPSILON && Math.abs(next.vPitch) < REST_EPSILON && Math.abs(next.vYaw - IDLE_YAW) < REST_EPSILON) {
      return afterReorient({ ...next, vYaw: IDLE_YAW, vPitch: 0, pitch: REST_PITCH }, mode);
    }
    return { ...next, phase: "reorient", t: 0, from: { pitch: next.pitch, vPitch: next.vPitch, vYaw: next.vYaw, timeScale } };
  }

  // reorient: pitch eases home on a smootherstep, so it starts and lands without a jolt. The fling's
  // leftover pitch velocity rides in on a term that decays over REORIENT_HANDOFF and is eased out
  // with the rest, so the hand-off keeps the velocity it had and the landing is still. The spin
  // eases to the idle rate on the same curve.
  const t = s.t + sdt;
  const u = Math.min(t / REORIENT_DURATION, 1);
  const { pitch: p0, vPitch: v0, vYaw: w0 } = s.from;
  const ease = smootherstep(u);
  const easeRate = smootherstepSlope(u) / REORIENT_DURATION;
  const decay = Math.exp(-t / REORIENT_HANDOFF);
  const carry = v0 * REORIENT_HANDOFF * (1 - decay);
  const pitch = clampPitch(p0 + (REST_PITCH - p0) * ease + carry * (1 - ease));
  const vPitch = (REST_PITCH - p0) * easeRate + v0 * decay * (1 - ease) - carry * easeRate;
  const vYaw = w0 + (IDLE_YAW - w0) * ease;
  const next = { ...s, timeScale, t, pitch, vPitch, vYaw, yaw: s.yaw + vYaw * sdt };
  return u < 1 ? next : afterReorient({ ...next, pitch: REST_PITCH, vPitch: 0, vYaw: IDLE_YAW }, mode);
}

function afterReorient(s: Spin, mode: SpinMode): Spin {
  return mode === "play" ? { ...s, phase: "spin", t: 0 } : startStop(s);
}

function startStop(s: Spin): Spin {
  return { ...s, phase: "stop", t: 0, from: { ...s.from, timeScale: s.timeScale } };
}
